import React from "react"
import { Icons, ToastContainer as V11ToastContainer, toast as v11toast } from "react-toastify/unstyled"

/**
 * react-toastify 11, with version 8's markup and container defaults.
 *
 * vite.config.mjs points `react-toastify` here, so the app's 221 importers and its toast
 * styles (scss/base.scss) stay as they were. Everything is react-toastify 11 except:
 * - toast content is wrapped in v8's body - <div class="Toastify__toast-body"> holding the
 *   icon and a <div> with the message - which the app's styles target (v11 renders bare
 *   content with the icon outside);
 * - the container keeps v8's defaults where v11 changed them (closeOnClick, draggable).
 * Imports from react-toastify/unstyled: v11's default entry injects its CSS at the end of
 * <head>, over the app's styles; v8 never did (App.js imports ReactToastify.css).
 */
export * from "react-toastify/unstyled"

function V8Body({ content, toastProps }) {
    const type = toastProps?.type || "default"
    const theme = toastProps?.theme || "light"
    const icon = type !== "default" && Icons[type] ? Icons[type]({ theme, type }) : null

    return (
        <div role="alert" className="Toastify__toast-body">
            {icon && <div className="Toastify__toast-icon Toastify--animate-icon Toastify__zoom-enter">{icon}</div>}
            <div>{content}</div>
        </div>
    )
}

// How long a toast stays: long enough to read it. The containers' 3 s is the floor, so a short
// line is as before; every ~16 characters (about a line) add a second, up to 10 s.
const MIN_MS = 3000
const MAX_MS = 10000
const BASE_MS = 1500
const PER_CHAR_MS = 60

// The words in a toast's content (a string, or JSX with text in it); function content is unknown
const textOf = (node) => {
    if (typeof node === "string" || typeof node === "number") return String(node)
    if (Array.isArray(node)) return node.map(textOf).join(" ")
    return React.isValidElement(node) ? textOf(node.props.children) : ""
}

/** The options with an autoClose from the text's length, unless the caller chose one (even `false`) */
export const withReadingTime = (content, options) => {
    if (options?.autoClose !== undefined) return options
    const chars = textOf(content).trim().length
    if (!chars) return options
    return { ...options, autoClose: Math.min(MAX_MS, Math.max(MIN_MS, BASE_MS + chars * PER_CHAR_MS)) }
}

// v8 called function content with { closeToast, toastProps }; v11 passes a superset
const wrap = (content) => {
    const Wrapped = (props) => (
        <V8Body content={typeof content === "function" ? content(props) : content} toastProps={props.toastProps} />
    )
    return Wrapped
}

export const toast = Object.assign(
    (content, options) => v11toast(wrap(content), withReadingTime(content, options)),
    v11toast,
    {
        success: (content, options) => v11toast.success(wrap(content), withReadingTime(content, options)),
        error: (content, options) => v11toast.error(wrap(content), withReadingTime(content, options)),
        warning: (content, options) => v11toast.warning(wrap(content), withReadingTime(content, options)),
        warn: (content, options) => v11toast.warn(wrap(content), withReadingTime(content, options)),
        // Don't call this one: it's a regular toast, as the app has no info style
        info: (content, options) => v11toast(wrap(content), withReadingTime(content, options)),
        update: (id, options = {}) =>
            v11toast.update(id, options.render !== undefined ? { ...options, render: wrap(options.render) } : options),
    },
)

// v8 defaults that v11 changed; the icon is drawn inside the v8 body above
export function ToastContainer(props) {
    return <V11ToastContainer closeOnClick draggable {...props} icon={false} />
}
